const BRAZIL_TZ = 'America/Sao_Paulo';
const BRAZIL_OFFSET = '-03:00';

const HAS_TZ_SUFFIX = /(?:[zZ]|[+-]\d{2}:\d{2})$/;

const brazilDateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: BRAZIL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
});

function normalizeApiDateTimeInput(value: string): string {
  return value
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1');
}

/**
 * Datas da API .NET (BrazilNow / Unspecified) chegam como relógio de Brasília sem offset.
 * Anexar `Z` tratava como UTC e exibia −3h no pipe `brDate`.
 */
export function parseApiDateTime(value?: string | null): Date | null {
  const raw = (value || '').trim();
  if (!raw) {
    return null;
  }
  const normalized = normalizeApiDateTimeInput(raw);

  const midnightUtc = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?Z$/i.exec(normalized);
  if (midnightUtc) {
    const d = new Date(`${midnightUtc[1]}T00:00:00${BRAZIL_OFFSET}`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (HAS_TZ_SUFFIX.test(normalized)) {
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(normalized);
  const withTz = isDateOnly ? normalized : `${normalized}${BRAZIL_OFFSET}`;
  const d = new Date(withTz);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Normaliza data vinda da API para ISO com offset `-03:00`. */
export function normalizeApiDateTimeToBrazil(value?: string | null): string | undefined {
  const raw = (value || '').trim();
  if (!raw) {
    return undefined;
  }
  const d = parseApiDateTime(raw);
  return d ? toBrazilOffsetIsoString(d) : raw;
}

/** Exibe data/hora no fuso de Brasília (`America/Sao_Paulo`). */
export function formatBrazilDateTime(value?: string | null, fallback = '—'): string {
  const d = parseApiDateTime(value);
  if (!d) {
    return (value || '').trim() || fallback;
  }
  return brazilDateTimeFormatter.format(d);
}

/**
 * ISO 8601 com offset fixo -03:00 (horário de Brasília), ex.: `2026-05-20T16:20:25.788-03:00`.
 */
export function toBrazilOffsetIsoString(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BRAZIL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';

  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}.${ms}${BRAZIL_OFFSET}`;
}

/**
 * ISO “parede” de Brasília sem sufixo Z/offset — o backend grava Unspecified.
 * Ex.: 2026-09-14T15:50:00
 */
export function toBrazilWallClockString(date: Date = new Date()): string {
  const withOffset = toBrazilOffsetIsoString(date);
  return withOffset.replace(/\.\d{3}[+-]\d{2}:\d{2}$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
}

/** Junta yyyy-MM-dd + HH:mm no relógio de Brasília (sem Z). */
export function combineBrazilDateAndTime(
  dateValue?: string | null,
  timeValue?: string | null,
): string | null {
  const raw = (dateValue || '').trim();
  if (!raw) return null;

  if (/T\d{2}:\d{2}/.test(raw) && !/T00:00:00/.test(raw.split('.')[0])) {
    return raw.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '').replace(/\.\d+$/, '');
  }

  const datePart = raw.split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

  const timeRaw = (timeValue || '').trim();
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(timeRaw);
  if (!match) {
    return toBrazilWallClockString(new Date());
  }

  const hh = String(Number(match[1])).padStart(2, '0');
  const mm = match[2];
  const ss = match[3] ?? '00';
  return `${datePart}T${hh}:${mm}:${ss}`;
}

